import React, {
  FunctionComponent,
  ReactElement,
  useRef,
  useState,
} from "react";
import styled from "@mui/material/styles/styled";
import DesktopExpandView from "../../../themeComponents/DesktopExpandView";
import { useScreenSize } from "../util/useScreenSize";
import { MobileExpandView } from "../../../themeComponents/MobileExpandView";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { useReactToPrint } from "react-to-print";
import ExportDataHeader from "./exportDataHeader";
import ExportDataGroupSelect from "./exportDataGroupSelect";
import ExportDataButtons from "./exportDataButtons";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";
import useExportData from "./useExportData";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import Toast from "fe-web-components/layout/toast";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import usePatientSummaryByCase from "./usePatientSummaryByCase";

//region [[ Styles ]]
interface ContextTextProps {
  child?: string;
}

const DataWrapper = styled("div")(({ theme }) => ({
  margin: "10px 30px",
  [theme.breakpoints.down(MobileView)]: {
    margin: "10px 20px",
    marginBottom: "160px",
  },
}));

const ContentText = styled("div")((props: ContextTextProps) => ({
  margin: props.child === "true" ? "2px 10px" : "10px",
  fontSize: "16px",
  whiteSpace: "pre-wrap",
}));

const ContentTitle = styled(ContentText)({
  fontWeight: "bold",
  marginTop: "15px",
});

const DesktopDataContent = styled("div")({
  overflow: "auto",
});

const DesktopButtonsWrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  boxShadow: "0px -2px 5px rgba(0, 0, 0, 0.08)",
});

const MobileDataContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "rgba(255, 255, 255, 0.63)", //TODO: move to rosebudColorMap
  boxShadow: `0 4px 16px 0 ${RosebudColorMap.uglyBlue4}`,
});

const MobileButtonsWrapper = styled(DesktopButtonsWrapper)(({ theme }) => ({
  position: "sticky",
  bottom: "-2px",

  [theme.breakpoints.down(MobileView)]: {
    position: "fixed",
    bottom: "75px",
    width: "100%",
    marginLeft: "-7px",
  },

  marginLeft: "7px",
  backgroundColor: "white",
}));

//endregion [[ Styles ]]

//region [[ Props ]]
//endregion [[ Props ]]

//region [[ Functions ]]
const showDataGroupSelect = getFeatureFlags("agentSummaryCheckboxes");
const useSolverSummary = getFeatureFlags("agentSummaryFromSolver");
const TOAST_TEXT = "Patient summary was copied to your clipboard";
//endregion [[ Functions ]]

const ExportDataWrapper: FunctionComponent<{
  Buttons: ReactElement;
  children: ReactElement | ReactElement[];
}> = ({ children, Buttons }) => {
  const { isMobile } = useScreenSize();

  return !isMobile ? (
    <DesktopExpandView flexDirection={"column"} borderRadius={"0"}>
      <DesktopDataContent>{children}</DesktopDataContent>
      <DesktopButtonsWrapper>{Buttons}</DesktopButtonsWrapper>
    </DesktopExpandView>
  ) : (
    <MobileExpandView showTopArea={false}>
      <MobileDataContent>{children}</MobileDataContent>
      <MobileButtonsWrapper>{Buttons}</MobileButtonsWrapper>
    </MobileExpandView>
  );
};

export const ExportData = () => {
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isToastOpen, setIsToastOpen] = useState<boolean>(false);

  const {
    dataSections,
    activeDataGroups,
    setActiveDataGroups,
  } = useExportData();

  const { summary } = usePatientSummaryByCase();

  const componentRef = useRef(null);

  const handlePrint = () => {
    evt({
      category: CATEGORY.AGENT,
      action: ACTION.PRINT_EXPORT_DATA_CLICKED,
    });
    print();
  };

  const print = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => setIsPrinting(true),
    onAfterPrint: () => setIsPrinting(false),
  });

  const copyToClipboard = () => {
    evt({
      category: CATEGORY.AGENT,
      action: ACTION.COPY_EXPORT_DATA_CLICKED,
    });

    const text = document.getElementById("copy");
    let range, selection;
    selection = window.getSelection();
    range = document.createRange();
    range.selectNodeContents(text);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
    window.getSelection().removeAllRanges();
    setIsToastOpen(true);
  };

  const onCheckboxChange = (name, checked) => {
    setActiveDataGroups((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  if (useSolverSummary && !summary) {
    return <ThreeDotsLoader />;
  }

  return (
    <ExportDataWrapper
      Buttons={
        <ExportDataButtons
          handlePrint={handlePrint}
          copyToClipboard={copyToClipboard}
        />
      }
    >
      <div ref={componentRef}>
        <ExportDataHeader />
        {showDataGroupSelect && !isPrinting && (
          <ExportDataGroupSelect
            activeDataGroups={activeDataGroups}
            onCheckboxChange={onCheckboxChange}
          />
        )}
        <DataWrapper id={"copy"}>
          {useSolverSummary &&
            summary &&
            summary.sections.map((section, index) => (
              <React.Fragment key={index}>
                <ContentTitle>{section.title}</ContentTitle>
                <ContentText>{section.content}</ContentText>
              </React.Fragment>
            ))}
          {!useSolverSummary &&
            dataSections.map((section, index) => {
              if (activeDataGroups[section.dataGroup] === false) return null;
              return section.isGroupTitle ? (
                <ContentTitle key={index}>{section.title}</ContentTitle>
              ) : section.isParagraphTitle ? (
                <React.Fragment key={index}>
                  <ContentTitle>{section.title}</ContentTitle>
                  <ContentText child={section.isChild ? "true" : "false"}>
                    {section.text || "None"}
                  </ContentText>
                </React.Fragment>
              ) : (
                <ContentText
                  key={index}
                  child={section.isChild ? "true" : "false"}
                >
                  {section.title ? `${section.title}: ` : ""}
                  {section.text || "None"}
                </ContentText>
              );
            })}
        </DataWrapper>
      </div>
      <Toast
        text={TOAST_TEXT}
        open={isToastOpen}
        onClose={() => setIsToastOpen(false)}
      />
    </ExportDataWrapper>
  );
};
